import type { ReactNode } from "react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "secondaryBordered"
  | "outline"
  | "ghost"
  | "ghostNeutral"
  | "text"
  | "link"
  | "signature"
  | "danger";
export type ButtonSize = "sm" | "md" | "lg";

/** Keep loading and disabled states synchronized so the spinner and click handling stay consistent. */
export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variant?: ButtonVariant;
  size?: ButtonSize;

  leadingIcon?: ReactNode;

  trailingIcon?: ReactNode;

  loading?: boolean;

  fullWidth?: boolean;
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs gap-1.5",
  md: "h-10 px-5 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2",
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "text-content-inverse before:bg-content-primary hover:before:opacity-90",
  secondary: "text-content-primary before:bg-surface-secondary hover:before:bg-surface-tertiary",

  secondaryBordered:
    "text-content-primary before:bg-surface-primary before:border before:border-border-input hover:before:border-content-tertiary hover:before:bg-surface-base",
  outline: "text-content-primary before:border before:border-border-strong hover:before:bg-surface-secondary",
  ghost: "text-content-secondary hover:text-content-primary hover:before:bg-surface-secondary",

  ghostNeutral:
    "text-content-secondary hover:text-content-primary hover:before:bg-surface-base aria-expanded:text-content-primary aria-expanded:before:bg-surface-base",

  text: "text-content-tertiary hover:text-content-primary",

  link: "text-content-tertiary underline-offset-2 hover:text-content-primary hover:underline",
  signature: "text-content-on-brand before:bg-brand-gradient hover:before:shadow-[var(--glow-brand)]",

  danger: "text-white before:bg-red-600 hover:before:bg-red-700",
};

export function Button({
  variant = "primary",
  size = "md",
  leadingIcon,
  trailingIcon,
  loading = false,
  disabled = false,
  fullWidth = false,
  children,
  type = "button",
  ...rest
}: Readonly<ButtonProps>) {
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      data-testid="button"
      data-variant={variant}
      disabled={isDisabled}
      className={[
        "cursor-pointer",
        "relative isolate inline-flex items-center justify-center rounded-lg font-semibold tracking-normal whitespace-nowrap",
        "transition-colors duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
        "before:absolute before:inset-0 before:-z-10 before:rounded-[inherit]",
        "before:transition-all before:duration-[180ms] before:ease-[cubic-bezier(0.16,1,0.3,1)]",
        "active:before:scale-[0.97]",
        "btn-focus-ring",
        "disabled:pointer-events-none disabled:opacity-50",
        fullWidth ? "flex w-full" : "",
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {loading ? <ButtonSpinner /> : leadingIcon}
      {size === "sm" ? <span className="pt-px">{children}</span> : children}
      {!loading && trailingIcon}
    </button>
  );
}

function ButtonSpinner() {
  return (
    <span
      data-testid="button-spinner"
      className="h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin"
      style={{ opacity: 0.7 }}
    />
  );
}
